import { forwardRef, useCallback } from 'react'
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  Navigate as RouterNavigate,
  useNavigate as useRouterNavigate,
  type LinkProps,
  type NavLinkProps,
  type NavigateProps,
  type NavigateFunction,
  type NavigateOptions,
  type To,
} from 'react-router-dom'
import { useEmbedded, resolvePath } from '../lib/embed'

/**
 * Context-aware drop-in replacements for react-router's navigation primitives.
 *
 * Every internal link in the app is written against the canonical `/projects/*` paths. When the
 * page is rendered inside the embeddable `/p/*` iframe these primitives transparently rewrite the
 * target to the matching `/p/*` path (and vice-versa), so a link never breaks the iframe out into
 * the full app. Import `Link`/`NavLink`/`Navigate`/`useNavigate` from here instead of from
 * `react-router-dom` and navigation "just works" in both route trees.
 */

function resolveTo(to: To, embedded: boolean): To {
  if (typeof to === 'string') return resolvePath(to, embedded)
  if (to && typeof to === 'object' && to.pathname) {
    return { ...to, pathname: resolvePath(to.pathname, embedded) }
  }
  return to
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link({ to, ...rest }, ref) {
  const embedded = useEmbedded()
  return <RouterLink ref={ref} to={resolveTo(to, embedded)} {...rest} />
})

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink({ to, ...rest }, ref) {
  const embedded = useEmbedded()
  return <RouterNavLink ref={ref} to={resolveTo(to, embedded)} {...rest} />
})

export function Navigate({ to, ...rest }: NavigateProps) {
  const embedded = useEmbedded()
  return <RouterNavigate to={resolveTo(to, embedded)} {...rest} />
}

/** Like react-router's `useNavigate`, but rewrites path targets for the current route tree. */
export function useNavigate(): NavigateFunction {
  const navigate = useRouterNavigate()
  const embedded = useEmbedded()
  return useCallback((to: To | number, options?: NavigateOptions) => {
    if (typeof to === 'number') return navigate(to)
    return navigate(resolveTo(to, embedded), options)
  }, [navigate, embedded]) as NavigateFunction
}
